import React, { forwardRef, Ref, useRef, useState } from 'react';
import classnames from 'classnames';
import ArrowUpSVG from '../../../../../images/svg/icons8-arrow-up';
import ChevronSVG from '../../../../../images/svg/icons8-expand-arrow';
import { getConnectionItemLabelText, showNewUniqueConnection } from '../../../../../infrastructure/utils/connections.utils';
import { Popover } from '../../../../global/popover/popover';
import './connection-dropdown.scss';
import Text from '../../../../global/text/text';
import { ConnectionType, IConnectionAttendeeDTO, IConnectionDetailsDTO, IConnectionGroupDTO, IConnectionOption } from '../../../../../api/connections.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import DefaultButton from '../../../../global/default-button/default-button';
import { editText } from '../../../../../constants/common-labels.constants';
import { NEW_UNIQUE_CONNECTION_TEXT } from '../../_connections/connections.texts';
import { ConnectionVersion } from '../../../../../constants/common.constants';
import { makeAssignToConnectionsInPopover } from '../audiobridge-connection-dropdown/audio-bridge-connections.presentation';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { FormattedMessage } from 'react-intl';

export type DropdownPropsType = {
    connections: IConnectionDetailsDTO[];
    derivedConnectionId: number | null;
};

interface IProps {
    optionValue?: string | null;
    isInheritOptionExist?: boolean;
    isLabelVisibleCondition?: boolean;
    optionsList?: IConnectionOption[];
    initOption?: IConnectionOption;
    onChange: (newConnectionId: number | null, connectionOption?: string) => void;
    getOptionItem?: (newConnectionId: number | null) => IConnectionOption;
    derivedConnection?: IConnectionDetailsDTO | null;
    assignedConnectionId?: number | null;
    meetingConnections?: IConnectionDetailsDTO[];
    connectionGroups?: IConnectionGroupDTO[];
    onAttendeeLevelDropdown?: boolean;
    editConnection?: (connection: IConnectionDetailsDTO) => void;
    knovioReplayConnection?: IConnectionDetailsDTO;
    disabledDropDownConnection?: boolean;
    connectionVersion?: ConnectionVersion;
    selectedAttendee?: Partial<IConnectionAttendeeDTO> | IAttendeeOrganizationDTO[] | IConnectionAttendeeDTO[];
    cssClass?: string;
}
interface IOpenFormInPopOverBtnProps extends IProps {
    optionsList?: IConnectionOption[];
    onChange: (
        newConnectionId: number | null,
        connectionOption?: string
    ) => void;
    assignedConnectionId?: number | null;
    meetingConnections?: IConnectionDetailsDTO[];
    isOpen: boolean;
    setModelOpen: (value) => void;
    setStatusModalOpen: (value) => void;
}

export const OpenFormInPopOverBtn = makeAssignToConnectionsInPopover(
    forwardRef((props: IOpenFormInPopOverBtnProps, ref: Ref<HTMLInputElement>) => {
        return <DefaultButton
            text={<FormattedMessage {...NEW_UNIQUE_CONNECTION_TEXT} />}
            ref={ref}
            modifier={'slim'}
            className={classnames('connection-dropdown__audiobridge-list__add-button', {
                'connection-dropdown__option--selected': props.isOpen
            })}
        />;
    })
);

export const unselectedOptionValue = 'Choose connection…';
export const unselectedOptionId = null;
export const unselectedOption = { id: unselectedOptionId, value: unselectedOptionValue };


export const uniqueJoiningLink = 'Unique Joining Link';

const ConnectionDropdownPresentation = (props: IProps) => {
    const statusButtonRef = useRef(null);
    const [isStatusModalOpen, setStatusModalOpen] = useState(false);
    const [isAudioBridgeModalOpen, setAudioBridgeModalOpen] = useState(false);
    const {
        isLabelVisibleCondition,
        onChange,
        assignedConnectionId,
        derivedConnection,
        meetingConnections,
        onAttendeeLevelDropdown,
        editConnection,
        knovioReplayConnection,
        disabledDropDownConnection = false,
        connectionVersion,
        selectedAttendee,
        connectionGroups = [],
        cssClass = ''
    } = props;

    const isNewDailInButton = showNewUniqueConnection(meetingConnections) && connectionVersion === ConnectionVersion.V2;

    const handleEditConnection = (e, connectionId: number): void => {
        e.stopPropagation();

        const connection = meetingConnections.find(el => el.meetingConnectionId === connectionId);

        editConnection(connection);
        setStatusModalOpen(prev => !prev);
    };

    const connectionOptionsMap = meetingConnections
        .filter(item => !item.cancelled && item.meetingConnectionId !== null)
        .reduce((_map, item) => {
            _map[item.meetingConnectionId] = {
                id: item.meetingConnectionId,
                value: item.type === ConnectionType.UniqueJoiningLink ? uniqueJoiningLink : getConnectionItemLabelText(item)
            };
            return _map;
        }, {}) as Record<number, IConnectionOption>;

    const connectionGroupOptionsMap = connectionGroups.reduce((_map, group) => {
        _map[group.meetingConnectionId] = { id: group.meetingConnectionId, value: getConnectionItemLabelText(group) };
        return _map;
    }, {}) as Record<number, IConnectionOption>;

    const optionsList: IConnectionOption[] = [
        unselectedOption,
        ...Object.values(connectionOptionsMap),
        ...Object.values(connectionGroupOptionsMap)
    ];

    const selectedOptionId = assignedConnectionId || unselectedOptionId;

    const selectedOptionValue = (
        knovioReplayConnection && getConnectionItemLabelText(knovioReplayConnection) ||
        assignedConnectionId && (connectionOptionsMap?.[assignedConnectionId]?.value || connectionGroupOptionsMap?.[assignedConnectionId]?.value) ||
        derivedConnection && getConnectionItemLabelText(derivedConnection) ||
        unselectedOptionValue
    );

    const cancelledConnection = (assignedConnectionId && meetingConnections.find(i => i.meetingConnectionId === assignedConnectionId) ||
        derivedConnection && derivedConnection);

    const isCancelled = cancelledConnection?.cancelled ?? false;

    const cancelledOption = isCancelled && {
        id: cancelledConnection.meetingConnectionId,
        value: getConnectionItemLabelText(cancelledConnection)
    };

    const isConnectionDerived = Boolean(knovioReplayConnection || (!assignedConnectionId && derivedConnection));
    const isConnectionInConnectionGroups = (connectionGroupId: number) => {
        return connectionGroups.some(connectionGroup => connectionGroup.meetingConnectionId === connectionGroupId);
    };

    const handleOnOptionSelect = (newOptionId: number, connectionOption?: string) => {
        onChange(newOptionId, connectionOption);
        setStatusModalOpen(false);
    };

    const handleOutSideClick = e => {
        if (!e.composedPath()
            .filter(elem => elem.tagName === 'DIV')
            .some(elem => elem.classList.value.includes('react-tiny-popover-container'))
        ) {
            setStatusModalOpen(false);
        }
    };

    return (
        <div className={classnames('connection-dropdown', cssClass, {
            'connection-dropdown--selected': !disabledDropDownConnection && !!selectedOptionId,
            'connection-dropdown--disabled': meetingConnections.length === 0 || disabledDropDownConnection,
            'connection-dropdown--active': isStatusModalOpen,
        })} ref={statusButtonRef}>
            <div className='connection-dropdown__wrapper'
                onClick={!disabledDropDownConnection
                    ? () => setStatusModalOpen(prevState => optionsList.length > 1 && !prevState)
                    : undefined
                }>
                {isConnectionDerived ? <ArrowUpSVG /> : null}
                <div className='connection-dropdown__team'>
                    <Text
                        className={classnames('connection-dropdown__label', {
                            'connection-dropdown__label--visible': isLabelVisibleCondition || isStatusModalOpen,
                            'connection-dropdown__label--delete': isCancelled
                        })}
                        message={isCancelled ? cancelledOption.value : selectedOptionValue}
                        withEllipsis
                    />
                   { !disabledDropDownConnection && <ChevronSVG /> }
                </div>
            </div>
            <Popover
                padding={0}
                isOpen={isStatusModalOpen}
                buttonRef={statusButtonRef}
                onClickOutside={e => handleOutSideClick(e)}
            >
                <div className='connection-dropdown__options'>
                    {optionsList.map(el =>
                        <div
                            className={classnames('connection-dropdown__option', {
                                'connection-dropdown__option--selected': selectedOptionId === el.id && !isAudioBridgeModalOpen,
                                'connection-dropdown__option--connection-group': isConnectionInConnectionGroups(el.id)
                            })}
                            key={el.id}
                            onClick={
                                onAttendeeLevelDropdown ?
                                () => handleOnOptionSelect(el.id, el.value) :
                                () => handleOnOptionSelect(el.id)
                            }
                        >
                            <Text
                                id={el.id?.toString()}
                                message={el.value}
                                description='Dropdown option button text'
                                withEllipsis
                            />
                            {hasPermission(Permission.CONNECTIONS_MANAGEMENT) && el.value === uniqueJoiningLink &&
                                <DefaultButton
                                    onClick={e => handleEditConnection(e, el.id)}
                                    modifier='thin'
                                    disabled={isCancelled}
                                    text={editText()}
                                />
                            }
                        </div>
                    )}
                    {isNewDailInButton && selectedAttendee && <OpenFormInPopOverBtn
                        {...props}
                        isOpen={isAudioBridgeModalOpen}
                        setModelOpen={setAudioBridgeModalOpen}
                        setStatusModalOpen={setStatusModalOpen}
                        attendeeInfo={selectedAttendee}
                    />}
                </div>
            </Popover>
        </div>
    );
};
ConnectionDropdownPresentation.defaultProps = {
    meetingConnections: []
};

export { ConnectionDropdownPresentation };
